import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, X, Sparkles, Pencil, Save, TrendingUp, TrendingDown, Minus, Check, AlertCircle, Activity } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import api from '../lib/api';
import { semaphoreVsBaseline } from '../lib/semaphore';
import { fmt } from '../lib/calendarFormat';
import { CalendarForecastBanner } from './CalendarForecastBanner';
import { CalendarForecastChart } from './CalendarForecastChart';

/** Etichette umane per il semaforo (mappate al delta vs budget). */
const SEMAPHORE_LABEL = {
  green: 'sopra target',
  yellow: 'in linea',
  red: 'sotto target',
};

const MONTH_NAMES = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const KPI_ORDER = [
  ['total_sales', 'Vendite totali', 'currency'],
  ['service_revenue', 'Ricavo servizi', 'currency'],
  ['product_revenue', 'Ricavo prodotti', 'currency'],
  ['total_appointments', 'Appuntamenti', 'integer'],
  ['avg_ticket', 'Ticket medio', 'currency'],
  ['available_hours', 'Ore disponibili', 'decimal'],
  ['booked_hours', 'Ore prenotate', 'decimal'],
  ['occupancy_pct', 'Occupazione', 'percentage'],
  ['new_clients', 'Nuovi clienti', 'integer'],
  ['returning_clients', 'Clienti di ritorno', 'integer'],
  ['review_count', 'Recensioni', 'integer'],
];

/** Group flat daily rows into a `{date: {kpi_key: value}}` map. */
function groupRowsByDate(rows, scenario = 'actual') {
  const out = {};
  for (const r of rows || []) {
    if (r.scenario !== scenario) continue;
    if (!out[r.date]) out[r.date] = {};
    out[r.date][r.kpi_key] = r.value;
  }
  return out;
}

/** Days array for a given month, padded so the grid starts on Monday. */
function buildCalendarGrid(year, month /* 1..12 */) {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  // 0=Sun..6=Sat → shift so Monday=0
  const startWeekday = (first.getUTCDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) {
    cells.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  // pad to a full week
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const SEMAPHORE_BG = {
  green: 'bg-emerald-100 hover:bg-emerald-200 border-emerald-300',
  yellow: 'bg-amber-100 hover:bg-amber-200 border-amber-300',
  red: 'bg-red-100 hover:bg-red-200 border-red-300',
  neutral: 'bg-slate-50 hover:bg-slate-100 border-slate-200',
};

/**
 * `onMonthYearChange(newMonth, newYear)`: callback opzionale che propaga
 * la navigazione tra mesi (frecce prev/next) al parent (es. StoreDashboard)
 * che a sua volta sincronizza il filtro periodo globale dell'header.
 * Refactor 2026-05-08, idea_0bbfb95e4434.
 */
export default function CalendarTab({ storeId, storeName, initialMonth, initialYear, onMonthYearChange }) {
  const today = new Date();
  const [month, setMonth] = useState(initialMonth || (today.getMonth() + 1));
  const [year, setYear] = useState(initialYear || today.getFullYear());
  const [rows, setRows] = useState([]);
  const [monthlyBudget, setMonthlyBudget] = useState(null);
  const [dailyBudgetTargets, setDailyBudgetTargets] = useState({});
  const [closedDays, setClosedDays] = useState(new Set());
  const [anomalies, setAnomalies] = useState({});
  const [forecast, setForecast] = useState({ forecasts: {}, month_total: null, today: null });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Quando il parent cambia il filtro periodo (header date picker → useEffect
  // del parent ricomputa initialMonth/initialYear), sincronizziamo il
  // calendario. Senza questo, navigare via header non aggiornava la vista.
  useEffect(() => {
    if (initialMonth && initialMonth !== month) setMonth(initialMonth);
    if (initialYear && initialYear !== year) setYear(initialYear);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMonth, initialYear]);

  // Quando la navigazione interna (frecce prev/next) cambia mese/anno,
  // notifica il parent così l'header date picker resta allineato.
  useEffect(() => {
    if (onMonthYearChange) onMonthYearChange(month, year);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  useEffect(() => {
    setLoading(true);
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const from = `${year}-${String(month).padStart(2, '0')}-01`;
    const to   = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    api.get(`/stores/${storeId}/kpi-daily`, { params: { from_: from, to } })
      .then(res => {
        setRows(res.data.rows || []);
        // Pick the budget for the visible month (single entry expected for a month-wide range)
        const mb = (res.data.monthly_budgets || []).find(b => b.year === year && b.month === month);
        setMonthlyBudget(mb?.values || null);
        setDailyBudgetTargets(res.data.daily_budget_targets || {});
        setClosedDays(new Set(res.data.closed_days || []));
        setAnomalies(res.data.anomalies || {});
        setForecast(res.data.forecast || { forecasts: {}, month_total: null, today: null });
      })
      .catch(() => {
        setRows([]); setMonthlyBudget(null);
        setDailyBudgetTargets({}); setClosedDays(new Set());
        setAnomalies({});
        setForecast({ forecasts: {}, month_total: null, today: null });
      })
      .finally(() => setLoading(false));
  }, [storeId, month, year, refreshTick]);

  const grid = useMemo(() => buildCalendarGrid(year, month), [year, month]);
  const actualByDate = useMemo(() => groupRowsByDate(rows, 'actual'), [rows]);
  const budgetByDate = useMemo(() => groupRowsByDate(rows, 'budget'), [rows]);

  // Fallback target giornaliero quando il backend non fornisce ancora la
  // distribuzione pesata (server-side). Si deriva dividendo il budget mensile
  // per i giorni del mese visibile — comportamento legacy ancora utile per
  // istanze più vecchie / test offline.
  const derivedDailyBudget = useMemo(() => {
    if (!monthlyBudget) return null;
    const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
    if (days <= 0) return null;
    const out = {};
    for (const [k, v] of Object.entries(monthlyBudget)) {
      if (typeof v === 'number' && !Number.isNaN(v)) out[k] = v / days;
    }
    return out;
  }, [monthlyBudget, year, month]);

  /** Returns the budget object to use for a specific ISO date. Prefers the
   * server-side weighted distribution; falls back to the flat division when
   * the API doesn't expose `daily_budget_targets`. */
  const budgetForDate = (iso) => {
    if (!iso) return null;
    if (budgetByDate[iso]) return budgetByDate[iso];
    if (dailyBudgetTargets[iso]) return dailyBudgetTargets[iso];
    return derivedDailyBudget;
  };

  // Hero stats
  const stats = useMemo(() => {
    const dates = Object.keys(actualByDate);
    const totalSales = dates.reduce((acc, d) => acc + (actualByDate[d].total_sales || 0), 0);
    const totalApps = dates.reduce((acc, d) => acc + (actualByDate[d].total_appointments || 0), 0);
    return {
      daysWithData: dates.length,
      totalDaysInMonth: grid.filter(Boolean).length,
      totalSales,
      avgTicket: totalApps > 0 ? totalSales / totalApps : 0,
    };
  }, [actualByDate, grid]);

  const goPrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const goNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-5" data-testid="calendar-tab">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl font-semibold tracking-tight">Vista giornaliera</h2>
          <p className="text-sm text-slate-500 mt-0.5">{storeName} · clicca un giorno per il dettaglio KPI</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 border border-slate-200" data-testid="calendar-month-switcher">
            <button onClick={goPrevMonth} className="p-2 hover:bg-slate-100" data-testid="calendar-prev-month" aria-label="Mese precedente">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-4 text-sm font-semibold tabular-nums w-44 text-center">
              {MONTH_NAMES[month - 1]} {year}
            </span>
            <button onClick={goNextMonth} className="p-2 hover:bg-slate-100" data-testid="calendar-next-month" aria-label="Mese successivo">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Hero stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="calendar-stats">
        <StatTile label="Giorni con dati" value={`${stats.daysWithData} / ${stats.totalDaysInMonth}`} />
        <StatTile label="Vendite del mese" value={fmt(stats.totalSales, 'currency')} />
        <StatTile label="Ticket medio mese" value={fmt(stats.avgTicket, 'currency')} />
        <StatTile label="Stato" value={loading ? 'Caricamento…' : (stats.daysWithData === 0 ? 'Nessun import' : 'Aggiornato')} />
      </div>

      {/* Forecast banner — visible only when the visible month overlaps "today
          or future" AND forecast math could compute a complete projection.
          Mostra: consuntivo a oggi + proiezione fine mese basata su rolling
          avg same-weekday 8 settimane (vedi services/forecast.py). Se lo
          storico è troppo corto per coprire ogni giorno futuro, banner si
          nasconde silenziosamente — preferiamo "non lo sappiamo" a un
          numero parziale che inganna. */}
      <CalendarForecastBanner forecast={forecast} monthlyBudget={monthlyBudget} />

      {/* Daily forecast line chart — visualizzazione complementare al banner:
          mostra il flusso giornaliero del mese in corso, con il dato attuale
          come linea solida fino a oggi e il forecast come linea tratteggiata
          oltre. Stessa stagionalità giorno-settimana visibile a colpo d'occhio
          (i sabati picco, i lunedì valle), così l'AM coglie pattern e
          discontinuità che la singola cifra "totale fine mese" nasconde. */}
      <CalendarForecastChart
        storeId={storeId}
        rows={rows}
        forecast={forecast}
        dailyBudgetTargets={dailyBudgetTargets}
        gridDates={grid.filter(Boolean)}
      />

      {/* Calendar grid + side panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        <div className="bg-white border border-slate-200 p-2 md:p-4">
          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-1 mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {WEEKDAYS.map(w => <div key={w} className="text-center">{w}</div>)}
          </div>
          {/* Day cells */}
          <div className="grid grid-cols-7 gap-1">
            {grid.map((iso, i) => (
              <DayCell
                key={iso || `pad-${i}`}
                iso={iso}
                actual={iso ? actualByDate[iso] : null}
                budget={iso ? budgetForDate(iso) : null}
                isFuture={iso && iso > todayIso}
                isClosed={iso && closedDays.has(iso)}
                isSelected={iso && iso === selectedDate}
                anomaly={iso ? anomalies[iso] : null}
                forecastValue={iso ? forecast.forecasts?.[iso] : null}
                onClick={() => iso && setSelectedDate(iso)}
              />
            ))}
          </div>
          {/* Legend */}
          <div className="mt-4 flex items-center gap-x-3 gap-y-1 text-[10px] text-slate-500 flex-wrap">
            <LegendDot cls="bg-emerald-200 border-emerald-400" label="Sopra budget" />
            <LegendDot cls="bg-amber-200 border-amber-400" label="In linea" />
            <LegendDot cls="bg-red-200 border-red-400" label="Sotto budget" />
            <LegendDot cls="bg-slate-100 border-slate-300" label="Senza target" />
            <span className="inline-flex items-center gap-1.5 ml-2">
              <span className="inline-flex items-center justify-center w-3 h-3 bg-emerald-600 text-white">
                <Check className="w-2 h-2" strokeWidth={3} />
              </span>
              Importato
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-flex items-center justify-center w-3 h-3 bg-slate-200 text-slate-500">
                <AlertCircle className="w-2 h-2" strokeWidth={2.5} />
              </span>
              Mancante
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-flex items-center justify-center w-3 h-3 bg-fuchsia-600 text-white">
                <Activity className="w-2 h-2" strokeWidth={3} />
              </span>
              Anomalia
            </span>
            <span className="inline-flex items-center gap-1.5" title="Proiezione basata su media giorno-settimana ultime 8 settimane">
              <span className="inline-flex items-center justify-center w-3 h-3 bg-slate-100 border border-dashed border-slate-400 text-slate-500 italic font-mono text-[7px]">
                f
              </span>
              Forecast
            </span>
          </div>
        </div>

        <DayDetailPanel
          date={selectedDate}
          actual={selectedDate ? actualByDate[selectedDate] : null}
          budget={selectedDate ? budgetForDate(selectedDate) : null}
          isClosed={selectedDate ? closedDays.has(selectedDate) : false}
          anomaly={selectedDate ? anomalies[selectedDate] : null}
          actualByDate={actualByDate}
          onClose={() => setSelectedDate(null)}
          onEdit={() => setEditingDate(selectedDate)}
        />
      </div>

      {/* Manual entry dialog — reuses the existing API endpoint
          POST /api/stores/{id}/kpi-daily/{date} */}
      <DayEntryDialog
        storeId={storeId}
        storeName={storeName}
        date={editingDate}
        currentValues={editingDate ? actualByDate[editingDate] : null}
        onClose={() => setEditingDate(null)}
        onSaved={() => {
          setEditingDate(null);
          setRefreshTick(t => t + 1);
        }}
      />

      {/* Import sheet rimosso 2026-05-08 (idea_0bbfb95e4434): l'import
          giornaliero ora vive solo in Impostazioni → Import Dati per
          centralizzare il flusso. Niente più bottone "Importa giornaliero"
          dalla pagina negozio. */}
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="border border-slate-200 bg-white p-3">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{label}</p>
      <p className="font-heading text-lg font-bold mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}

function LegendDot({ cls, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 border ${cls}`} />
      {label}
    </span>
  );
}

function DayCell({ iso, actual, budget, isFuture, isClosed, isSelected, anomaly, forecastValue, onClick }) {
  if (!iso) return <div className="h-14 md:h-20" aria-hidden="true" />;
  const day = parseInt(iso.slice(8, 10), 10);

  const sales = actual?.total_sales;
  const targetSales = budget?.total_sales;
  const hasData = sales != null;
  let semaphore = 'neutral';
  if (sales != null && targetSales != null && targetSales > 0) {
    semaphore = semaphoreVsBaseline(sales, targetSales);
  }
  // Closed days: visually distinct neutral background — no red semaphore even
  // if `actual` is missing, since "no data" is expected on a closed day.
  const closedCls = 'bg-slate-50 border-slate-200 text-slate-400';
  const bg = isFuture
    ? 'bg-white border-slate-100 text-slate-300'
    : (isClosed ? closedCls : SEMAPHORE_BG[semaphore]);
  const ringCls = isSelected ? 'ring-2 ring-slate-900 ring-offset-1' : '';

  // Status icon: ✓ se importato (giorni passati o oggi), ⚠ se mancante per giorni
  // passati. Per i giorni futuri non mostriamo niente: non sono "in ritardo".
  // Per i giorni di chiusura non mostriamo "mancante" (l'assenza è prevista).
  let statusIcon = null;
  if (!isFuture && !isClosed) {
    statusIcon = hasData ? 'check' : 'missing';
  }

  // Anomaly tooltip: spiega in linguaggio naturale perché la giornata è
  // marcata. Nel rebrand 2026-05-08 abbiamo sostituito σ con la deviazione %
  // (più immediata per gli AM). Flagghiamo SOLO i giorni sotto-media; i
  // sopra-media restano un'icona-free perché "buona notizia" non è un alert.
  const anomalyTooltip = anomaly
    ? `Vendite ${Math.round(Math.abs(anomaly.pct_dev) * 100)}% sotto la media stesso giorno-settimana `
      + `(€${Math.round(anomaly.value).toLocaleString('it-IT')} vs media €${Math.round(anomaly.mean).toLocaleString('it-IT')}, `
      + `${anomaly.baseline_size} giorni di baseline)`
    : null;

  return (
    <button
      onClick={onClick}
      disabled={isFuture}
      className={`relative h-14 md:h-20 border ${bg} ${ringCls} text-left p-1 md:p-1.5 transition-all disabled:cursor-not-allowed`}
      data-testid={`calendar-day-${iso}`}
    >
      {/* Top row: numero giorno (sx) + icona stato (dx) */}
      <div className="flex items-start justify-between gap-0.5">
        <span className="text-[11px] md:text-xs font-semibold tabular-nums">{day}</span>
        <div className="flex items-center gap-0.5">
          {anomaly && (
            <span
              className="inline-flex items-center justify-center w-3 h-3 md:w-3.5 md:h-3.5 bg-fuchsia-600 text-white"
              title={anomalyTooltip}
              data-testid={`calendar-day-${iso}-anomaly`}
            >
              <Activity className="w-2 h-2 md:w-2.5 md:h-2.5" strokeWidth={3} />
            </span>
          )}
          {statusIcon === 'check' && (
            <span
              className="inline-flex items-center justify-center w-3 h-3 md:w-3.5 md:h-3.5 bg-emerald-600 text-white"
              title="Dato importato"
              data-testid={`calendar-day-${iso}-check`}
            >
              <Check className="w-2 h-2 md:w-2.5 md:h-2.5" strokeWidth={3} />
            </span>
          )}
          {statusIcon === 'missing' && (
            <span
              className="inline-flex items-center justify-center w-3 h-3 md:w-3.5 md:h-3.5 bg-slate-200 text-slate-500"
              title="Dato mancante per questo giorno"
              data-testid={`calendar-day-${iso}-missing`}
            >
              <AlertCircle className="w-2 h-2 md:w-2.5 md:h-2.5" strokeWidth={2.5} />
            </span>
          )}
        </div>
      </div>

      {hasData && (
        <div className="absolute bottom-0.5 left-1 right-1 text-[9px] md:text-[10px] tabular-nums leading-tight">
          <p className="font-mono font-semibold truncate">{fmt(sales, 'currency-compact')}</p>
          {targetSales > 0 && (
            <p className="hidden md:block text-[9px] text-slate-600 truncate">vs {fmt(targetSales, 'currency-compact')}</p>
          )}
        </div>
      )}
      {!hasData && isClosed && !isFuture && (
        <div
          className="absolute bottom-0.5 left-1 right-1 text-[9px] md:text-[10px] uppercase tracking-wider text-slate-400 italic"
          data-testid={`calendar-day-${iso}-closed`}
        >
          chiuso
        </div>
      )}
      {/* Forecast su giorno futuro: tono grigio chiaro, italic e prefisso "f"
          per distinguerlo a colpo d'occhio dai valori actual. Se il backend
          non ha potuto calcolare il forecast (storico troppo corto) il valore
          è null e non mostriamo niente — meglio "vuoto" che un numero finto. */}
      {!hasData && isFuture && !isClosed && forecastValue != null && (
        <div
          className="absolute bottom-0.5 left-1 right-1 text-[9px] md:text-[10px] tabular-nums leading-tight italic text-slate-400 font-mono"
          title="Proiezione (media giorno-settimana ultime 8 sett.)"
          data-testid={`calendar-day-${iso}-forecast`}
        >
          f {fmt(forecastValue, 'currency-compact')}
        </div>
      )}
    </button>
  );
}

function DayDetailPanel({ date, actual, budget, isClosed, anomaly, actualByDate, onClose, onEdit }) {
  // Hooks MUST run on every render → compute trend before any early return.
  const sales = actual?.total_sales;
  const trend = useMemo(() => {
    if (!date || sales == null || !actualByDate) return null;
    const previousSales = Object.entries(actualByDate)
      .filter(([d, vals]) => d < date && vals?.total_sales != null)
      .sort(([a], [b]) => b.localeCompare(a))     // desc by date
      .slice(0, 3)
      .map(([, vals]) => vals.total_sales);
    if (previousSales.length === 0) return null;
    const avg = previousSales.reduce((a, b) => a + b, 0) / previousSales.length;
    if (avg === 0) return null;
    const pct = ((sales - avg) / avg) * 100;
    return { pct, sample: previousSales.length, avg };
  }, [date, sales, actualByDate]);

  if (!date) {
    return (
      <div className="border border-dashed border-slate-200 bg-slate-50 p-6 flex items-center justify-center text-center" data-testid="calendar-empty-panel">
        <div>
          <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Seleziona un giorno per vedere<br />tutti i KPI di quella data</p>
        </div>
      </div>
    );
  }

  const formattedDate = new Date(`${date}T00:00:00`).toLocaleDateString('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const hasActual = actual && Object.keys(actual).length > 0;
  const budgetSales = budget?.total_sales;

  const hasBudget = sales != null && budgetSales != null && budgetSales > 0;
  const sem = hasBudget ? semaphoreVsBaseline(sales, budgetSales) : null;
  const deltaPct = hasBudget ? ((sales - budgetSales) / budgetSales) * 100 : null;
  const medalCls = sem === 'green'
    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
    : sem === 'red'
      ? 'bg-red-100 text-red-800 border-red-300'
      : sem === 'yellow'
        ? 'bg-amber-100 text-amber-800 border-amber-300'
        : 'bg-slate-50 text-slate-500 border-slate-200';

  const trendDir = trend == null
    ? null
    : Math.abs(trend.pct) < 3 ? 'flat' : (trend.pct > 0 ? 'up' : 'down');
  const trendCls = trendDir === 'up'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : trendDir === 'down'
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-slate-50 text-slate-600 border-slate-200';
  const TrendIcon = trendDir === 'up' ? TrendingUp : trendDir === 'down' ? TrendingDown : Minus;

  return (
    <div className="bg-white border border-slate-200" data-testid="calendar-detail-panel">
      <div className="flex items-start justify-between p-4 border-b border-slate-200 bg-slate-50 gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Dettaglio giornaliero</p>
          <p className="font-heading text-base font-semibold capitalize mt-0.5 truncate">{formattedDate}</p>
          {isClosed && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 mt-2 text-[10px] font-semibold uppercase tracking-wider border bg-slate-100 text-slate-600 border-slate-300"
              data-testid="calendar-detail-closed-badge"
            >
              Chiuso
            </span>
          )}
          {(hasBudget || trend) && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {hasBudget && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${medalCls}`}
                  data-testid="calendar-detail-medal"
                  title={`Vendite: ${sales.toFixed(0)}€ vs target ${budgetSales.toFixed(0)}€`}
                >
                  {deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}%
                  <span className="text-[9px] font-normal opacity-70 ml-0.5">vs budget</span>
                </span>
              )}
              {trend && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${trendCls}`}
                  data-testid="calendar-detail-trend"
                  title={`Media ultimi ${trend.sample} ${trend.sample === 1 ? 'giorno' : 'giorni'} con dati: ${trend.avg.toFixed(0)}€`}
                >
                  <TrendIcon className="w-3 h-3" />
                  {trend.pct >= 0 ? '+' : ''}{trend.pct.toFixed(1)}%
                  <span className="text-[9px] font-normal opacity-70 ml-0.5">vs media {trend.sample}gg</span>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] uppercase tracking-wider font-semibold text-slate-700 hover:bg-slate-200 border border-slate-300"
            data-testid="calendar-detail-edit"
            title="Inserisci o modifica i dati di questo giorno"
          >
            <Pencil className="w-3 h-3" />
            {hasActual ? 'Modifica' : 'Inserisci'}
          </button>
          <button onClick={onClose} className="p-1 hover:bg-slate-200" data-testid="calendar-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="p-4">
        {anomaly && (
          <div
            className="mb-3 px-3 py-2 border border-fuchsia-300 bg-fuchsia-50 text-fuchsia-900 flex items-start gap-2"
            data-testid="calendar-detail-anomaly"
          >
            <Activity className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
            <div className="text-xs leading-snug">
              <p className="font-semibold uppercase tracking-wider text-[10px]">
                Anomalia rilevata · sotto la media
              </p>
              <p className="mt-0.5">
                Vendite{' '}
                <span className="font-mono font-semibold">
                  {fmt(anomaly.value, 'currency')}
                </span>
                {' '}vs media stesso giorno-settimana{' '}
                <span className="font-mono">{fmt(anomaly.mean, 'currency')}</span>
                {' '}<span className="font-semibold">
                  ({Math.round(anomaly.pct_dev * 100)}%)
                </span>,{' '}
                {anomaly.baseline_size} giorni di baseline. Verifica import o eventi straordinari.
              </p>
            </div>
          </div>
        )}
        {!hasActual ? (
          <div className="text-center py-8">
            <Pencil className="w-6 h-6 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Nessun dato per questa data</p>
            <p className="text-[11px] text-slate-400 mt-1">Importa via Fresha o inserisci manualmente</p>
          </div>
        ) : (
          <ul className="space-y-2.5" data-testid="calendar-detail-kpis">
            {KPI_ORDER.map(([key, label, format]) => {
              const a = actual?.[key];
              const b = budget?.[key];
              if (a == null && b == null) return null;
              let sem = null;
              if (a != null && b != null && b !== 0) {
                sem = semaphoreVsBaseline(a, b);
              }
              return (
                <li key={key} className="flex items-baseline justify-between gap-2 py-1 border-b border-slate-100 last:border-0">
                  <span className="text-xs text-slate-600">{label}</span>
                  <div className="text-right">
                    <p className="font-mono font-semibold text-sm tabular-nums">
                      {a != null ? fmt(a, format) : <span className="text-slate-300">—</span>}
                    </p>
                    {b != null && (
                      <p className="text-[10px] text-slate-400 tabular-nums">
                        target {fmt(b, format)}{sem && (
                          <span className={`ml-1 ${sem === 'green' ? 'text-emerald-700' : sem === 'red' ? 'text-red-700' : 'text-amber-700'}`}>
                            · {SEMAPHORE_LABEL[sem]}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}


/** Manual entry dialog. Edits the additive primitives only — derived KPIs
 *  (avg_ticket, occupancy_pct, …) are recomputed automatically by the backend
 *  monthly aggregator on save. */
const EDITABLE_FIELDS = [
  { key: 'total_sales',         label: 'Vendite totali',     suffix: '€', step: '0.01' },
  { key: 'service_revenue',     label: 'Ricavo servizi',     suffix: '€', step: '0.01' },
  { key: 'product_revenue',     label: 'Ricavo prodotti',    suffix: '€', step: '0.01' },
  { key: 'total_appointments',  label: 'Appuntamenti',       suffix: '',  step: '1' },
  { key: 'services_sold',       label: 'Servizi venduti',    suffix: '',  step: '1' },
  { key: 'products_sold',       label: 'Prodotti venduti',   suffix: '',  step: '1' },
  { key: 'available_hours',     label: 'Ore disponibili',    suffix: 'h', step: '0.1' },
  { key: 'booked_hours',        label: 'Ore prenotate',      suffix: 'h', step: '0.1' },
  { key: 'new_clients',         label: 'Nuovi clienti',      suffix: '',  step: '1' },
  { key: 'returning_clients',   label: 'Clienti di ritorno', suffix: '',  step: '1' },
  { key: 'total_clients',       label: 'Totale clienti',     suffix: '',  step: '1' },
  // `review_count` rimosso dal manuale: il valore è auto-popolato
  // giornalmente dalla Google Places API (vedi services/google_places.py).
];

function DayEntryDialog({ storeId, storeName, date, currentValues, onClose, onSaved }) {
  const open = !!date;
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset form whenever the date changes (so opening a different day
  // doesn't leak the previous day's input).
  useEffect(() => {
    if (!date) return;
    const next = {};
    for (const f of EDITABLE_FIELDS) {
      const v = currentValues?.[f.key];
      next[f.key] = v == null ? '' : String(v);
    }
    setValues(next);
    setError(null);
  }, [date, currentValues]);

  const handleSave = async () => {
    setSaving(true); setError(null);
    const payload = {};
    for (const f of EDITABLE_FIELDS) {
      const raw = values[f.key];
      if (raw === '' || raw == null) continue;
      const n = parseFloat(String(raw).replace(',', '.'));
      if (Number.isNaN(n)) {
        setError(`Valore non valido per "${f.label}"`);
        setSaving(false);
        return;
      }
      payload[f.key] = n;
    }
    if (Object.keys(payload).length === 0) {
      setError('Inserisci almeno un valore.');
      setSaving(false);
      return;
    }
    try {
      await api.post(`/stores/${storeId}/kpi-daily/${date}`, payload);
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const formattedDate = date
    ? new Date(`${date}T00:00:00`).toLocaleDateString('it-IT', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      })
    : '';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="day-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {currentValues && Object.keys(currentValues).length > 0 ? 'Modifica' : 'Inserisci'} dati — {storeName}
          </DialogTitle>
          <DialogDescription className="capitalize">
            {formattedDate}. Lascia vuoti i campi che non vuoi aggiornare; quelli compilati sovrascrivono i valori esistenti. I KPI derivati (ticket medio, % occupazione…) vengono ricalcolati automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
          {EDITABLE_FIELDS.map(f => (
            <label key={f.key} className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                {f.label}{f.suffix && <span className="text-slate-300 ml-1">({f.suffix})</span>}
              </span>
              <input
                type="number"
                step={f.step}
                min="0"
                value={values[f.key] ?? ''}
                onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                placeholder="—"
                className="px-3 py-2 border border-slate-300 focus:border-slate-900 focus:outline-none font-mono text-sm tabular-nums"
                data-testid={`day-entry-${f.key}`}
              />
            </label>
          ))}
        </div>

        {error && (
          <div className="px-3 py-2 border border-red-200 bg-red-50 text-xs text-red-800" data-testid="day-entry-error">
            {error}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} className="rounded-none" data-testid="day-entry-cancel">
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-black text-white hover:bg-slate-900 rounded-none" data-testid="day-entry-save">
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Salvataggio…' : 'Salva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
